import { Tooltip, Typography } from 'antd';
import type { ReactNode } from 'react';

const { Text } = Typography;

/**
 * Roughly what one character costs, in pixels, at the table's font size.
 *
 * Used to turn a cell width into “how many characters could be showing”. It is
 * an average over ordinary written English — `i` is nothing and `W` is double
 * — so it is approximate by construction, which is why it is only ever used as
 * a floor and never as the only answer. See the component below.
 */
const PIXELS_PER_CHARACTER = 7;

export interface FreeTextProps {
  /** What somebody typed. Null and empty both render the dash. */
  children: string | null | undefined;
  /** How wide the cell may grow before the text is cut. */
  maxWidth?: number;
  /** Shown when there is nothing to show. */
  placeholder?: ReactNode;
  /**
   * Renders bold, for a title or a name at the head of a stacked cell.
   *
   * Added when this moved beyond "Why" columns onto the names people type
   * into Settings (orbit-ui#103) — a long policy title widened its table
   * exactly as a long reason once did.
   */
  strong?: boolean;
}

/**
 * A cell holding something a person typed, in a table that has to stay usable.
 *
 * **Why this exists.** A reason, a remark, a rejection note — none of them has
 * a length anybody agreed to. One employee wrote a hundred and forty
 * characters into "Why", the column grew to fit it, the table grew past the
 * card, and the Approve and Reject buttons went off the right-hand edge. The
 * manager could read the reason perfectly well and had no way to act on it,
 * which is the worst shape a bug can take: the screen looks fine.
 *
 * So the column is capped and the text is cut with an ellipsis, and the whole
 * of it is in a tooltip on hover. Nothing is thrown away — it is one hover
 * away, and the buttons stay where they are.
 *
 * Every free-text cell in the product goes through here, because the next
 * table to grow a "Why" column is the next one to push its own buttons off
 * screen.
 */
export function FreeText({ children, maxWidth = 320, placeholder, strong = false }: FreeTextProps) {
  const text = children?.trim();
  if (!text) return <>{placeholder ?? <Text type="secondary">—</Text>}</>;

  /**
   * Two ways of asking “is this cut?”, because neither is enough alone.
   *
   * The exact answer is whether the text overflows its box, which antd will
   * measure for us — and a measurement needs a laid-out page, so in a test it
   * always says no. Trusting it alone means the rule is never verified, and a
   * tooltip that silently stops appearing looks exactly like one that works.
   *
   * The testable answer is a character count, and on its own it was wrong:
   * a flat sixty characters took no notice of how wide the cell was, so a
   * fifty-character rejection note in a 260px column was cut on screen and
   * offered no tooltip at all (orbit-ui#30). Scaling it to the width fixes
   * that, but it can still be fooled by unusually wide text.
   *
   * So: past the width-scaled floor we attach the tooltip ourselves, which is
   * what the tests pin. Below it, antd measures — covering the wide-glyph
   * strings the floor underestimates, and staying quiet in jsdom, where
   * nothing overflows and nothing should appear.
   */
  const certainlyCut = text.length * PIXELS_PER_CHARACTER > maxWidth;

  if (certainlyCut) {
    return (
      <Tooltip title={text}>
        <Text strong={strong} ellipsis style={{ maxWidth, display: 'block' }}>
          {text}
        </Text>
      </Tooltip>
    );
  }

  return (
    <Text strong={strong} ellipsis={{ tooltip: text }} style={{ maxWidth, display: 'block' }}>
      {text}
    </Text>
  );
}
